// app_client/src/components/accounts/AccountPortfolioCard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Check, PencilLine, Star, UserCheck, UserCog, Users, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useUserStore from '../../store/userStore';
import useAccountStore from '../../store/useAccountStore';

const getPersonName = (person, fallback = 'Atanmamış') => {
  if (!person || typeof person === 'string') return fallback;
  return [person?.name, person?.surname].filter(Boolean).join(' ') || person?.email || fallback;
};

const getPersonId = (person) => {
  if (!person) return '';
  if (typeof person === 'string') return person;
  return person._id || person.id || '';
};

const AccountPortfolioCard = ({ account, onAccountUpdate }) => {
  const { users, fetchUsers } = useUserStore();
  const { assignAccountResponsible } = useAccountStore();
  const assignedTo = account?.assignedTo;
  const assignedToId = getPersonId(assignedTo);
  const [isEditingRepresentative, setIsEditingRepresentative] = useState(false);
  const [selectedRepresentativeId, setSelectedRepresentativeId] = useState(assignedToId);

  useEffect(() => {
    if ((users || []).length === 0) {
      fetchUsers();
    }
  }, [fetchUsers, users]);

  useEffect(() => {
    setSelectedRepresentativeId(assignedToId);
  }, [assignedToId]);

  const assignedUserFromList = useMemo(() => (
    (users || []).find((user) => user?._id === assignedToId)
  ), [assignedToId, users]);

  const assignedName = getPersonName(
    typeof assignedTo === 'object' ? assignedTo : assignedUserFromList,
    assignedToId ? `Kullanıcı #${assignedToId.slice(-6)}` : 'Atanmadı'
  );

  const watchers = account?.watchers || [];
  const userOptions = useMemo(() => {
    const optionsById = new Map();

    [...users, assignedTo]
      .filter(Boolean)
      .forEach((user) => {
        const userId = getPersonId(user);
        if (!userId) return;
        optionsById.set(userId, {
          _id: userId,
          label: getPersonName(user, `Kullanıcı #${userId.slice(-6)}`)
        });
      });

    return Array.from(optionsById.values());
  }, [assignedTo, users]);

  const handleRepresentativeSave = async () => {
    if (!account?._id || !selectedRepresentativeId) {
      toast.error('Lütfen bir müşteri temsilcisi seçin.');
      return;
    }

    const result = await assignAccountResponsible(account._id, selectedRepresentativeId);
    if (result?.success) {
      toast.success('Müşteri temsilcisi güncellendi');
      setIsEditingRepresentative(false);
      if (typeof onAccountUpdate === 'function' && result.data) {
        onAccountUpdate(result.data);
      }
    } else {
      toast.error(result?.message || 'Müşteri temsilcisi değiştirilemedi');
    }
  };

  return (
    <div className="bg-white rounded-4xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-50 bg-slate-50/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
            <UserCog size={18} />
          </div>
          <span className="font-black text-slate-800 text-[10px] uppercase tracking-widest">Müşteri Temsilcisi</span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-tighter flex items-center gap-1.5">
            <UserCheck size={12} /> Müşteri Temsilcisi
          </label>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            {!isEditingRepresentative ? (
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
                    <UserCheck size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black text-slate-800">{assignedName}</p>
                    <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      {typeof assignedTo === 'object' ? assignedTo?.email || 'E-posta yok' : assignedToId || 'Atanmadı'}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsEditingRepresentative(true)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                >
                  <PencilLine size={12} />
                  Değiştir
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedRepresentativeId || ''}
                  onChange={(e) => setSelectedRepresentativeId(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 outline-none focus:border-blue-500"
                >
                  <option value="">Atanmadı</option>
                  {userOptions.map((user) => (
                    <option key={user._id} value={user._id}>{user.label}</option>
                  ))}
                </select>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRepresentativeSave}
                    disabled={!selectedRepresentativeId}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    <Check size={12} />
                    Kaydet
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRepresentativeId(assignedToId);
                      setIsEditingRepresentative(false);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    <X size={12} />
                    Vazgeç
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-tighter flex items-center gap-1.5">
            <Star size={12} /> İzleyen Personeller ({watchers?.length || 0})
          </label>
          
          <div className="flex flex-wrap gap-2">
            {watchers?.length > 0 ? (
              (watchers || []).map((watcher, idx) => (
                <div
                  key={watcher?._id || watcher || idx}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-600"
                >
                  <Users size={12} />
                  {[watcher?.name, watcher?.surname].filter(Boolean).join(' ') || getPersonName(watcher, `Kullanıcı #${idx + 1}`)}
                </div>
              ))
            ) : (
              <p className="text-[11px] text-slate-400 font-medium italic px-1">Henüz takibe alan yok.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPortfolioCard;
